import { z } from 'zod';

/**
 * Описание маршрута согласования. Маршрут собирается администратором в интерфейсе
 * и хранится как данные, поэтому новые сценарии не требуют правки кода:
 * этапы, условия, сроки, наблюдатели и действия после этапа задаются здесь.
 *
 * При запуске задачи описание «замораживается» в снимок: правки маршрута
 * не влияют на уже идущие согласования.
 */

const key = z
  .string()
  .min(1, 'Не заполнено')
  .max(64)
  .regex(/^[a-z0-9_-]+$/i, 'Только латиница, цифры, «-» и «_»');

/** Как согласует отдел или группа: любой один из них или каждый. */
export const POOL_MODES = ['any', 'all'] as const;
export type PoolMode = (typeof POOL_MODES)[number];
export const POOL_MODE_LABELS: Record<PoolMode, string> = {
  any: 'любой один',
  all: 'каждый',
};

/** Откуда берётся участник шага. Реальный пользователь определяется в момент запуска. */
export const participantSourceSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('user'), userId: z.uuid({ error: 'Выберите сотрудника' }) }),
  z.object({ type: z.literal('initiator') }),
  z.object({ type: z.literal('initiator_manager') }),
  z.object({ type: z.literal('initiator_department_head') }),
  z.object({ type: z.literal('department_head'), departmentId: z.uuid({ error: 'Выберите отдел' }) }),
  /**
   * Сотрудники отдела: «любой один» — решает первый, у остальных шаг снимается;
   * «каждый» — решение нужно от всех. Состав берётся на момент запуска, поэтому
   * уволенный сотрудник не ломает маршрут.
   */
  z.object({ type: z.literal('department'), departmentId: z.uuid({ error: 'Выберите отдел' }), mode: z.enum(POOL_MODES).default('any') }),
  /** Группа согласующих, которую администратор настраивает отдельно. */
  z.object({ type: z.literal('group'), groupId: z.uuid({ error: 'Выберите группу' }), mode: z.enum(POOL_MODES).default('any') }),
  /** Пользователь, выбранный в поле заявки (например, «Исполнитель»). */
  z.object({ type: z.literal('form_field'), field: key }),
  /**
   * Все активные сотрудники, кроме инициатора, — каждый получает свой шаг (ознакомление
   * с приказом). Состав берётся на момент запуска: новых сотрудников добавлять не нужно.
   */
  z.object({ type: z.literal('all_users'), mode: z.literal('all').default('all') }),
]);
export type ParticipantSource = z.infer<typeof participantSourceSchema>;
export type ParticipantSourceInput = z.input<typeof participantSourceSchema>;

export const PARTICIPANT_SOURCE_LABELS: Record<ParticipantSource['type'], string> = {
  user: 'Конкретный сотрудник',
  initiator: 'Инициатор',
  initiator_manager: 'Руководитель инициатора',
  initiator_department_head: 'Руководитель отдела инициатора',
  department_head: 'Руководитель отдела',
  department: 'Сотрудники отдела',
  group: 'Группа согласующих',
  form_field: 'Сотрудник из поля заявки',
  all_users: 'Все активные сотрудники',
};

/** Источник даёт несколько человек (отдел, группа, все сотрудники). */
export function isPoolSource(s: ParticipantSource): s is Extract<ParticipantSource, { type: 'department' | 'group' | 'all_users' }> {
  return s.type === 'department' || s.type === 'group' || s.type === 'all_users';
}

// ---- Условия ----

export const CONDITION_OPS = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'in', 'not_in', 'empty', 'not_empty'] as const;
/**
 * Поля для условий, которых нет в форме заявки: подставляются при запуске.
 * Инициатор — автор заявки, а в командировке — командируемый сотрудник.
 */
export const SYSTEM_CONDITION_FIELDS = {
  _initiator: 'Инициатор',
  _initiator_department: 'Отдел инициатора',
} as const;
export type SystemConditionField = keyof typeof SYSTEM_CONDITION_FIELDS;
export type ConditionOp = (typeof CONDITION_OPS)[number];

const scalar = z.union([z.string().max(500), z.number(), z.boolean(), z.null()]);

export type Condition =
  | { field: string; op: ConditionOp; value?: string | number | boolean | null | Array<string | number> }
  | { all: Condition[] }
  | { any: Condition[] }
  | { not: Condition };

export const conditionSchema: z.ZodType<Condition> = z.lazy(() =>
  z.union([
    z.object({
      field: z.string().min(1, 'Выберите поле для условия').max(64).regex(/^[a-z0-9_-]*$/i, 'Только латиница, цифры, «-» и «_»'),
      op: z.enum(CONDITION_OPS),
      value: z.union([scalar, z.array(z.union([z.string().max(500), z.number()])).max(100)]).optional(),
    }),
    z.object({ all: z.array(conditionSchema).min(1).max(20) }),
    z.object({ any: z.array(conditionSchema).min(1).max(20) }),
    z.object({ not: conditionSchema }),
  ]),
);

// ---- Шаги и этапы ----

/**
 * approve — согласование: «Согласовать», «Не согласовать», «Отклонить»;
 * task    — задача: участник выполняет действие (вложить скан, зарегистрировать)
 *           и отмечает «Выполнено». Отказать в задаче нельзя.
 */
export const STEP_KINDS = ['approve', 'task'] as const;
export type StepKind = (typeof STEP_KINDS)[number];
export const STEP_KIND_LABELS: Record<StepKind, string> = { approve: 'Согласование', task: 'Задача' };

export const FILE_KINDS = ['document', 'scan', 'attachment'] as const;
export type UploadFileKind = (typeof FILE_KINDS)[number];

export const stepSchema = z.object({
  key,
  participant: participantSourceSchema,
  kind: z.enum(STEP_KINDS).default('approve'),
  /** Шаг нужен, только если условие истинно (например, «договор ГПХ»). */
  when: conditionSchema.optional(),
  /** Инструкция участнику: что именно проверить или сделать. */
  instruction: z.string().max(2000).optional(),
  /** Срок на шаг в часах с момента, когда шаг стал активным. */
  deadlineHours: z.number().int().min(1).max(24 * 90).optional(),
  /** Кто контролирует выполнение: получает уведомления о начале, просрочке и выполнении. */
  controller: participantSourceSchema.optional(),
  /** Задача: отметить выполнение можно, только когда у документа есть файл этого вида. */
  requireFile: z.enum(FILE_KINDS).optional(),
  /** Задача: при выполнении обязателен комментарий (например, регистрационный номер). */
  requireComment: z.boolean().optional(),
  /** Задача: надпись на кнопке, например «Скан вложен», «Зарегистрировано». */
  actionLabel: z.string().trim().max(60).optional(),
  /**
   * Поля заявки, которые участник заполняет на этом шаге (номер договора, дата регистрации).
   * Без них не согласовать и не отметить выполнение; значения сохраняются в заявку.
   */
  fillFields: z.array(key).max(20).optional(),
});
export type StepDefinition = z.infer<typeof stepSchema>;

export const STAGE_MODES = ['parallel', 'sequential'] as const;
export type StageMode = (typeof STAGE_MODES)[number];

const notifyTarget = z.union([
  z.enum(['initiator', 'observers', 'participants', 'stage_participants']),
  participantSourceSchema,
]);
export type NotifyTarget = z.infer<typeof notifyTarget>;

const baseActions = [
  /** Уведомить (в приложении и по почте). */
  z.object({
    type: z.literal('notify'),
    when: conditionSchema.optional(),
    to: z.array(notifyTarget).min(1).max(20),
    message: z.string().trim().min(1, 'Напишите текст уведомления').max(1000),
  }),
  /** Отправить данные заявки во внешний сервис (фоновое задание с повторами). */
  z.object({
    type: z.literal('webhook'),
    when: conditionSchema.optional(),
    url: z.url({ protocol: /^https?$/ }).max(2000),
  }),
  /** Поставить подзадачу. */
  z.object({
    type: z.literal('create_subtask'),
    when: conditionSchema.optional(),
    title: z.string().trim().min(1, 'Укажите название подзадачи').max(500),
    assignee: participantSourceSchema,
    deadlineHours: z.number().int().min(1).max(24 * 90).optional(),
  }),
  /** Сформировать документ по шаблону (приказ, смета). */
  z.object({
    type: z.literal('generate_document'),
    when: conditionSchema.optional(),
    templateId: z.uuid(),
  }),
  /**
   * Сформировать лист согласования сейчас, по решениям, принятым к этому моменту.
   * Если маршрут продолжается задачами (скан, регистрация), лист не ждёт их завершения.
   */
  z.object({
    type: z.literal('approval_sheet'),
    when: conditionSchema.optional(),
  }),
  /**
   * Назначить замещение по полям заявки: кого замещают (поле или инициатор),
   * заместитель и даты. Например, тип «Замещение»: после согласования заместителем
   * замещение на эти даты создаётся само.
   */
  z.object({
    type: z.literal('create_substitution'),
    when: conditionSchema.optional(),
    /** Поле «кого замещают»; без него — инициатор. */
    absentField: key.optional(),
    deputyField: z.string().min(1, 'Выберите поле с заместителем').max(64),
    dateFromField: z.string().min(1, 'Выберите поле с датой начала').max(64),
    dateToField: z.string().min(1, 'Выберите поле с датой окончания').max(64),
    /** Поле с причиной (необязательно). */
    reasonField: key.optional(),
  }),
  /** Добавить наблюдателя по ходу маршрута. */
  z.object({
    type: z.literal('add_observer'),
    when: conditionSchema.optional(),
    participant: participantSourceSchema,
  }),
] as const;

/** Действия без вставки этапов: для вставляемых этапов и для завершения маршрута. */
export const simpleActionSchema = z.discriminatedUnion('type', [...baseActions]);
export type SimpleActionDefinition = z.infer<typeof simpleActionSchema>;

const stageFields = {
  key,
  name: z.string().trim().min(1, 'Укажите название этапа').max(200),
  mode: z.enum(STAGE_MODES),
  /** Этап выполняется, только если условие истинно; иначе пропускается. */
  when: conditionSchema.optional(),
  steps: z.array(stepSchema).max(50),
};

/** Этап, который можно вставить действием (без вложенных вставок — защита от бесконечных маршрутов). */
export const insertableStageSchema = z.object({
  ...stageFields,
  onComplete: z.array(simpleActionSchema).max(20).default([]),
});

export const actionSchema = z.discriminatedUnion('type', [
  ...baseActions,
  /** Вставить новый этап сразу после текущего. */
  z.object({
    type: z.literal('insert_stage'),
    when: conditionSchema.optional(),
    stage: insertableStageSchema,
  }),
]);
export type ActionDefinition = z.infer<typeof actionSchema>;

export const stageSchema = z.object({
  ...stageFields,
  onComplete: z.array(actionSchema).max(20).default([]),
});
export type StageDefinition = z.infer<typeof stageSchema>;
export type StageDefinitionInput = z.input<typeof stageSchema>;

/**
 * Проверки перед запуском и перезапуском согласования. Администратор может отключить
 * любую проверку в маршруте (enabled: false), не удаляя её.
 */
export const observerSchema = z.object({
  participant: participantSourceSchema,
  when: conditionSchema.optional(),
});
export type ObserverDefinition = z.infer<typeof observerSchema>;

/** Наблюдатель в описании: просто источник или источник с условием. */
export function normalizeObserver(o: ParticipantSource | ObserverDefinition): ObserverDefinition {
  return 'participant' in o ? o : { participant: o };
}

export const launchCheckSchema = z.discriminatedUnion('type', [
  /** У задачи должен быть файл нужного вида (например, скан). */
  z.object({
    type: z.literal('required_file'),
    kind: z.enum(FILE_KINDS),
    label: z.string().min(1).max(200),
    enabled: z.boolean().default(true),
  }),
  /**
   * В поле заявки выбран связанный документ (например, договор для оплаты счёта);
   * он должен быть согласован и/или иметь скан.
   */
  z.object({
    type: z.literal('linked_document'),
    field: key,
    label: z.string().min(1).max(200),
    requireApproved: z.boolean().default(true),
    requireScan: z.boolean().default(true),
    /** Документ можно не выбирать (например, договоры до определённой даты); выбран — проверяется. */
    skipIfEmpty: z.boolean().default(false),
    enabled: z.boolean().default(true),
  }),
]);
export type LaunchCheck = z.infer<typeof launchCheckSchema>;

export const routeDefinitionSchema = z
  .object({
    stages: z.array(stageSchema).min(1, 'Нужен хотя бы один этап').max(30),
    /**
     * Наблюдатели видят задачу и получают уведомления, но не согласуют. Наблюдатель
     * может быть с условием: «если договор ГПХ — добавить отдел кадров».
     */
    observers: z.array(z.union([participantSourceSchema, observerSchema])).max(50).default([]),
    /** Действия после окончательного согласования. */
    onApproved: z.array(simpleActionSchema).max(20).default([]),
    /** Действия после окончательного отказа. */
    onRejected: z.array(simpleActionSchema).max(20).default([]),
    launchChecks: z.array(launchCheckSchema).max(10).default([]),
  })
  .superRefine((def, ctx) => {
    const stageKeys = new Set<string>();
    def.stages.forEach((stage, i) => {
      if (stageKeys.has(stage.key)) {
        ctx.addIssue({ code: 'custom', path: ['stages', i, 'key'], message: 'Ключ этапа повторяется' });
      }
      stageKeys.add(stage.key);
      const stepKeys = new Set<string>();
      stage.steps.forEach((step, j) => {
        if (stepKeys.has(step.key)) {
          ctx.addIssue({
            code: 'custom',
            path: ['stages', i, 'steps', j, 'key'],
            message: 'Ключ шага повторяется внутри этапа',
          });
        }
        stepKeys.add(step.key);
      });
    });
  });
export type RouteDefinition = z.infer<typeof routeDefinitionSchema>;
export type RouteDefinitionInput = z.input<typeof routeDefinitionSchema>;

// ---- Решения ----

/**
 * approve — «Согласовать»;
 * decline — «Не согласовать»: голос против. Остальные участники этапа принимают решения,
 *           затем этап закрывается, следующие этапы не наступают и задача возвращается
 *           инициатору — «Не согласовано». После доработки он перезапускает, участвуют все
 *           заново (участники определяются заново), история прошлых кругов сохраняется;
 * reject  — «Отклонить»: окончательный отказ, согласование прекращается у всех.
 * Отдельно автор может «Прекратить» согласование у всех (cancel).
 */
export const DECISIONS = ['approve', 'decline', 'reject'] as const;
export type Decision = (typeof DECISIONS)[number];

export const DECISION_LABELS: Record<Decision, string> = {
  approve: 'Согласовать',
  decline: 'Не согласовать',
  reject: 'Отклонить',
};

export const INSTANCE_STATUS_LABELS = {
  in_progress: 'На согласовании',
  approved: 'Согласовано',
  /** Круг завершён, но кто-то не согласовал: автор дорабатывает и перезапускает. */
  returned: 'Не согласовано',
  rejected: 'Отклонено',
  cancelled: 'Прекращено',
} as const;
export type InstanceStatus = keyof typeof INSTANCE_STATUS_LABELS;
